import { lazy, Suspense } from "react";

import { cn } from "@/web/lib/styling";
import type { MapBounds, MapHandle, MapPin } from "@/web/components/styled/Map/types";

export type { MapBounds, MapHandle, MapPin };

// `React.lazy(() => import(...))` keeps the mapbox + react-map-gl tree in its
// own chunk (see `manualChunks` in vite.config.ts) so it only loads once a user
// actually lands on a map-bearing view.
const MapImpl = lazy(() => import("@/web/components/styled/Map/Map.impl"));

type MapProps<T> = {
  /** Points to plot. Each needs `id` + lat/long; styling/interaction are optional. */
  pins: MapPin<T>[];
  /**
   * Box the user cannot zoom out past. Panning stays unrestricted — we translate
   * this into a dynamic `minZoom` rather than Mapbox's `maxBounds` (which would
   * also lock panning). When omitted the map fits the pins on load and clamps
   * zoom-out to their padded extent.
   */
  maxBounds?: MapBounds;
  /** Padding (px) kept around the pins when fitting them on load. */
  fitPadding?: number;
  /** Show the zoom controls in the top-right corner. Defaults to `true`. */
  navigationControl?: boolean;
  /** Fired when the basemap (not a pin) is clicked — e.g. to clear a selection. */
  onBackgroundClick?: () => void;
  /** Applied to the map's container; height in particular should be set here. */
  className?: string;
  /** Overlays positioned over the map, e.g. a legend. */
  children?: React.ReactNode;
  /** Imperative handle for one-off camera moves (see {@link MapHandle}). */
  ref?: React.Ref<MapHandle>;
};

const DEFAULT_FIT_PADDING = 48;

/**
 * The app's shared map. Renders a list of {@link MapPin}s as opinionated square
 * markers over a consistent Mapbox style. Reach for this any time you need a map
 * with pins; only the mapbox chunk it lazily pulls in is map-specific.
 *
 * Give it a height via `className`; the container is `relative` so `children`
 * (legends, controls) can be absolutely positioned over the canvas.
 */
export function Map<T = unknown>({
  pins,
  maxBounds,
  fitPadding = DEFAULT_FIT_PADDING,
  navigationControl = true,
  onBackgroundClick,
  className,
  children,
  ref,
}: MapProps<T>) {
  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-none bg-withus-dark-blue/5",
        className,
      )}
    >
      <Suspense fallback={<MapFallback />}>
        <MapImpl
          ref={ref}
          pins={pins}
          maxBounds={maxBounds}
          fitPadding={fitPadding}
          navigationControl={navigationControl}
          onBackgroundClick={onBackgroundClick}
        />
      </Suspense>
      {children}
    </div>
  );
}

function MapFallback() {
  return (
    <div className="absolute inset-0 flex items-center justify-center text-sm text-withus-dark-blue/60">
      Loading map…
    </div>
  );
}
